import { motion } from "framer-motion";

const experiences = [
  {
    id: 1,
    title: "FullStack Developer",
    company: "aDrive, South Africa",
    period: "Aug 2023 - Oct 2024",
    description: "Cross-platform e-hailing app with payments, GPS, live updates, full CRM. Crafted from scratch.",
    logo: "/img/adrive.png",
    color: "#F97316",
    tech: ["React Native", "Node.js", "MySQL", "AWS", "Docker"],
  },
  {
    id: 2,
    title: "FullStack Developer",
    company: "AnnConnect, South Africa",
    period: "Jul 2023 - Oct 2024",
    description: "Offline exam system + invigilation app. Reliable under pressure.",
    logo: "/img/annconnect.png",
    color: "#DB2777",
    tech: ["Flutter", "Laravel", "MySQL", "PHP", "Docker"],
  },
  {
    id: 3,
    title: "Junior PHP Developer",
    company: "Property Studios, UK",
    period: "Mar 2022 - Jun 2022",
    description: "Maintained and extended legacy Yii2 CRM + React UI with modern practices.",
    logo: "/img/property_studios.jpg",
    color: "#F59E0B",
    tech: ["Yii2", "React", "PHP", "MySQL"],
  },
  {
    id: 4,
    title: "Freelance Developer",
    company: "Fiverr",
    period: "2020 - present",
    description: "Delivered custom EAs, web stacks, and mobile builds with high-quality standards.",
    logo: "/img/fiverr.jpg",
    color: "#22C55E",
    tech: ["JavaScript", "React", "PHP", "Node.js", "MetaTrader EAs"],
  },
];

export const Experience = () => {
  return (
    <section className="relative py-20 bg-white text-gray-800 overflow-hidden">
      {/* Subtle background blobs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-pink-100/20 rounded-full blur-[100px] animate-pulse-slow" />
        <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-blue-100/20 rounded-full blur-[100px] animate-pulse-slow delay-2000" />
      </div>

      <h2 className="text-5xl font-extrabold text-center mb-20 bg-clip-text text-transparent bg-gradient-to-r from-pink-400 via-purple-400 to-indigo-400">Work Experience</h2>

      <div className="max-w-6xl mx-auto relative px-4 md:px-0">
        {/* Timeline line */}
        <div className="hidden md:block absolute top-0 left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-pink-300 via-purple-300 to-indigo-300 rounded-full" />

        <div className="space-y-20">
          {experiences.map((exp, i) => (
            <TimelineItem key={exp.id} experience={exp} index={i} />
          ))}
        </div>
      </div>

      <style jsx>{`
        .animate-pulse-slow {
          animation: pulse 8s ease-in-out infinite;
        }
        @keyframes pulse {
          0%,
          100% {
            transform: scale(1);
            opacity: 0.3;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.5;
          }
        }
      `}</style>
    </section>
  );
};

const TimelineItem = ({ experience, index }) => {
  const isLeft = index % 2 === 0;

  return (
    <motion.div initial={{ opacity: 0, x: isLeft ? -50 : 50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: index * 0.2 }} viewport={{ once: true }} className={`relative md:flex md:items-center max-w-4xl mx-auto`}>
      {/* Dot */}
      <div className="hidden md:flex absolute top-10 left-1/2 -translate-x-1/2 z-10">
        <div className="w-5 h-5 rounded-full border-2 border-gray-300 bg-white shadow-sm" />
      </div>

      {/* Card */}
      <div className={`relative z-20 bg-white rounded-3xl p-8 max-w-md md:max-w-lg w-full shadow-lg border border-gray-200 ${isLeft ? "md:mr-auto md:text-right" : "md:ml-auto md:text-left"} hover:shadow-xl hover:scale-105 transition-transform cursor-default`}>
        <div className="flex items-center justify-between mb-6">
          <div className="w-20 h-20 rounded-full p-2 bg-white border border-gray-200 flex items-center justify-center shadow">
            <img src={experience.logo} alt={`${experience.company} logo`} className="object-contain w-full h-full rounded-full" />
          </div>

          <div className={`ml-4 flex-1 ${isLeft ? "text-right" : "text-left"}`}>
            <h3 className="text-2xl font-bold text-gray-800">{experience.title}</h3>
            <p className="text-lg font-semibold text-gray-600">{experience.company}</p>
            <time className="block mt-1 text-sm text-gray-400">{experience.period}</time>
          </div>
        </div>

        <p className="text-gray-700 leading-relaxed mb-4">{experience.description}</p>

        <div className={`flex flex-wrap gap-2 ${isLeft ? "justify-end" : "justify-start"}`}>
          {experience.tech.map((tech) => (
            <span key={tech} className="text-xs font-medium px-3 py-1 rounded-full bg-gray-100 border border-gray-200 text-gray-800">
              {tech}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  );
};
